import { useRef } from 'react';
import { toAbsoluteApiFileUrl } from '../lib/api';
import { AnalysisRegionBox } from './AnalysisRegionBox';
import type { AdminMlAnalysisRegion } from '../lib/ml-sources';

/**
 * ML 사진 썸네일 — **지운 사진의 시각 구별이 이 한 곳에서 정해진다.**
 *
 * ML 소스 목록과 점검 상세가 같은 규칙으로 그려야 하므로(둘 다 「전부 보여주되 무엇이 학습에
 * 들어가는지 알 수 있어야 한다」는 P5 계약을 따른다) 흐림·탈색·라벨을 각 화면이 따로 정하지 않는다.
 *
 * ⚠️ 「삭제됨」 라벨은 **조건부 렌더**다 — 존재가 곧 상태를 뜻하는 요소를 항상 렌더하고 문구만
 * 비우면, 그 손실이 spec에서 초록으로 지나간다.
 *
 * ⚠️ `region`은 **옵셔널이고 기본은 없음**이다 — 점검 상세(`pages/Inspections.tsx`)는 이 prop을
 * 넘기지 않으므로 그 화면은 무영향이다. 영역 오버레이를 여기 둔 이유는 **썸네일과 맥락 패널의
 * 확대 이미지가 같은 박스 규칙을 써야** 하기 때문이고(위 「한 곳에서 정해진다」와 같은 규율),
 * 박스 자체의 규칙은 `AnalysisRegionBox`가 소유한다.
 *
 * ⚠️ **여기 상자는 `aspect-square`이고 이미지는 `object-cover`다 — 즉 상자와 이미지의 종횡비가
 * 다르다.** 그래서 오버레이에 `objectFit="cover"`와 **이미지 ref**를 넘긴다: 박스는 백분율이
 * 아니라 **측정된 이미지 내용 상자**를 기준으로 그려진다(백분율은 두 비율이 같을 때만 맞고,
 * 그 전제가 틀려서 가로 사진의 박스가 가로로 밀려 있었다 — 2026-10-07 P4-h).
 * ⇒ `object-cover` ↔ `objectFit` prop은 **함께 바꿔야** 한다. 한쪽만 바꾸면 박스가 조용히
 * 어긋나고 그 증상은 「좌표가 틀렸다」처럼 보인다.
 */
export function MlPhotoThumbnail({
  url,
  thumbnailUrl,
  deletedAt,
  label,
  onClick,
  region,
}: {
  url: string;
  thumbnailUrl: string | null;
  deletedAt: string | null;
  /** 접근 가능한 이름 — 썸네일 이미지는 장식이라 `alt`를 비우고 **버튼이 이름을 갖는다**. */
  label: string;
  onClick: () => void;
  /** 중점 분석 영역(0~1 비율). 넘기지 않으면 오버레이를 그리지 않는다. */
  region?: AdminMlAnalysisRegion | null;
}) {
  const imageRef = useRef<HTMLImageElement>(null);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="relative block bg-gray-100 aspect-square w-full"
    >
      <img
        ref={imageRef}
        src={toAbsoluteApiFileUrl(thumbnailUrl ?? url)}
        alt=""
        loading="lazy"
        className={`w-full h-full object-cover ${deletedAt ? 'opacity-40 grayscale' : ''}`}
      />
      <AnalysisRegionBox region={region} imageRef={imageRef} objectFit="cover" />
      {deletedAt && (
        <span className="absolute inset-x-0 bottom-0 bg-red-600/85 text-white text-xs py-0.5 text-center">
          삭제됨
        </span>
      )}
    </button>
  );
}
